import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  query, 
  where, 
  orderBy,
  type QueryConstraint,
  Timestamp
} from 'firebase/firestore'
import { db } from '@/config/firebase'

// Optimized interfaces matching the new schema
interface Category {
  id: string
  slug: string
  icon: string
  color?: string
  order: number
  status: 'active' | 'archived' | 'draft'
  metadata: {
    createdAt: Date
    updatedAt: Date
    createdBy: string
    totalChallenges: number
  }
  seo?: {
    metaTitle?: string
    metaDescription?: string
  }
}

interface CategoryTranslation {
  id: string
  categoryId: string
  language: string
  name: string
  description: string
  shortDescription?: string
  keywords?: string[]
  seo?: {
    metaTitle?: string
    metaDescription?: string
  }
}

interface CategoryChallenge {
  id: string
  name: string
  description: string
  shortDescription?: string
  icon: string
  color?: string
  slug: string
  order: number
  status: string
  language: string
  totalChallenges: number
  createdAt: Date
  updatedAt: Date
}

/**
 * Category Repository for scalable database queries
 * 
 * This repository implements the schema with separate base entities
 * and translation documents for optimal performance at scale.
 */
export class CategoryRepository {
  private translationCache = new Map<string, Map<string, CategoryTranslation>>()
  private categoryCache = new Map<string, Category>()
  // private cacheExpiry = 5 * 60 * 1000 // 5 minutes

  /**
   * Get categories with translations in a single optimized query
   */
  async getCategories(language: string = 'en', includeInactive: boolean = false): Promise<CategoryChallenge[]> {
    try {
      // Build base query for categories
      const constraints: QueryConstraint[] = []
      
      if (!includeInactive) {
        constraints.push(where('status', '==', 'active'))
      }
      
      constraints.push(orderBy('order', 'asc'))

      // Execute optimized parallel queries
      const queryPromises = [
        getDocs(query(collection(db, 'categories'), ...constraints)),
        getDocs(query(
          collection(db, 'categoryTranslations'),
          where('language', '==', language)
        ))
      ]

      // Also fetch English translations as fallback if not requesting English
      if (language !== 'en') {
        queryPromises.push(
          getDocs(query(
            collection(db, 'categoryTranslations'),
            where('language', '==', 'en')
          ))
        )
      }

      const results = await Promise.all(queryPromises)
      const [categoriesSnapshot, translationsSnapshot, englishTranslationsSnapshot] = results

      // Build translation lookup maps
      const translationMap = new Map<string, CategoryTranslation>()
      const englishTranslationMap = new Map<string, CategoryTranslation>()

      translationsSnapshot.docs.forEach(doc => {
        const translation = { id: doc.id, ...doc.data() } as CategoryTranslation
        translationMap.set(translation.categoryId, translation)
      })

      if (englishTranslationsSnapshot) {
        englishTranslationsSnapshot.docs.forEach(doc => {
          const translation = { id: doc.id, ...doc.data() } as CategoryTranslation
          englishTranslationMap.set(translation.categoryId, translation)
        })
      }

      // Combine categories with translations (with fallback)
      const combinedCategories: CategoryChallenge[] = categoriesSnapshot.docs
        .map(doc => {
          const category = { id: doc.id, ...doc.data() } as Category
          let translation = translationMap.get(category.id)

          // Fallback to English if translation missing
          if (!translation && language !== 'en') {
            translation = englishTranslationMap.get(category.id)
            if (translation) {
              console.info(`Using English fallback for category ${category.slug || category.id} (${language} translation missing)`)
            }
          }

          if (!translation) {
            console.warn(`Missing translation for category ${category.slug || category.id} in both ${language} and English`)
            return null
          }

          return this.combineData(category, translation)
        })
        .filter((category): category is CategoryChallenge => category !== null)

      // Cache results
      this.updateCache(categoriesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Category)))
      this.updateTranslationCache(language, translationsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as CategoryTranslation)))

      return combinedCategories

    } catch (error) {
      console.error('Failed to get categories:', error)
      throw error
    }
  }

  /**
   * Get a single category by ID with translation
   */
  async getCategory(id: string, language: string = 'en'): Promise<CategoryChallenge | null> {
    try {
      // Check cache first
      const cachedCategory = this.categoryCache.get(id)
      const cachedTranslation = this.translationCache.get(language)?.get(id)

      if (cachedCategory && cachedTranslation) {
        return this.combineData(cachedCategory, cachedTranslation)
      }

      // Fetch from database
      const [categoryDoc, translationDoc] = await Promise.all([
        getDoc(doc(db, 'categories', id)),
        getDoc(doc(db, 'categoryTranslations', `${id}_${language}`))
      ])

      if (!categoryDoc.exists()) {
        return null
      }

      const category = { id: categoryDoc.id, ...categoryDoc.data() } as Category

      if (!translationDoc.exists()) {
        console.warn(`Missing translation for category ${id} in language ${language}`)
        // Try falling back to English
        if (language !== 'en') {
          return this.getCategory(id, 'en')
        }
        return null
      }

      const translation = { id: translationDoc.id, ...translationDoc.data() } as CategoryTranslation

      // Update cache
      this.categoryCache.set(id, category)
      if (!this.translationCache.has(language)) {
        this.translationCache.set(language, new Map())
      }
      this.translationCache.get(language)!.set(id, translation)

      return this.combineData(category, translation)

    } catch (error) {
      console.error(`Failed to get category ${id}:`, error)
      throw error
    }
  }

  /**
   * Get category by slug with translation
   */
  async getCategoryBySlug(slug: string, language: string = 'en'): Promise<CategoryChallenge | null> {
    try {
      // First get the category by slug
      const categoriesSnapshot = await getDocs(query(
        collection(db, 'categories'),
        where('slug', '==', slug),
        where('status', '==', 'active')
      ))

      if (categoriesSnapshot.empty) {
        return null
      }

      const categoryDoc = categoriesSnapshot.docs[0]
      const category = { id: categoryDoc.id, ...categoryDoc.data() } as Category

      // Get translation
      const translationDoc = await getDoc(doc(db, 'categoryTranslations', `${category.id}_${language}`))

      if (!translationDoc.exists()) {
        console.warn(`Missing translation for category ${category.id} in language ${language}`)
        // Try falling back to English
        if (language !== 'en') {
          return this.getCategoryBySlug(slug, 'en')
        }
        return null
      }

      const translation = { id: translationDoc.id, ...translationDoc.data() } as CategoryTranslation

      return this.combineData(category, translation)

    } catch (error) {
      console.error(`Failed to get category by slug ${slug}:`, error)
      throw error
    }
  }

  /**
   * Search categories by name in specific language
   */
  async searchCategories(searchTerm: string, language: string = 'en'): Promise<CategoryChallenge[]> {
    try {
      // Get all translations for the language and filter in memory
      const translationsSnapshot = await getDocs(query(
        collection(db, 'categoryTranslations'),
        where('language', '==', language)
      ))

      // Filter translations by search term
      const matchingTranslations = translationsSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() } as CategoryTranslation))
        .filter(translation => 
          translation.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          translation.description.toLowerCase().includes(searchTerm.toLowerCase())
        )

      if (matchingTranslations.length === 0) {
        return []
      }

      // Get the corresponding categories
      const categoryIds = matchingTranslations.map(t => t.categoryId)
      const categoriesSnapshot = await getDocs(query(
        collection(db, 'categories'),
        where('status', '==', 'active')
      ))

      const categories = categoriesSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() } as Category))
        .filter(category => categoryIds.includes(category.id))

      // Combine with translations
      const translationMap = new Map(matchingTranslations.map(t => [t.categoryId, t]))
      
      return categories
        .map(category => {
          const translation = translationMap.get(category.id)
          return translation ? this.combineData(category, translation) : null
        })
        .filter((category): category is CategoryChallenge => category !== null)
        .sort((a, b) => a.order - b.order)

    } catch (error) {
      console.error('Failed to search categories:', error)
      throw error
    }
  }

  /**
   * Get categories by IDs (batch load)
   */
  async getCategoriesByIds(ids: string[], language: string = 'en'): Promise<CategoryChallenge[]> {
    if (ids.length === 0) return []

    try {
      // Batch requests in chunks of 10 (Firestore limit for 'in' queries)
      const chunks = this.chunkArray(ids, 10)
      const allCategories: Category[] = []
      
      for (const chunk of chunks) {
        const categoriesSnapshot = await getDocs(query(
          collection(db, 'categories'),
          where('__name__', 'in', chunk)
        ))
        allCategories.push(...categoriesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Category)))
      }

      // Get translations for these categories
      const translationIds = ids.map(id => `${id}_${language}`)
      const translationChunks = this.chunkArray(translationIds, 10)
      const allTranslations: CategoryTranslation[] = []

      for (const chunk of translationChunks) {
        const translationsSnapshot = await getDocs(query(
          collection(db, 'categoryTranslations'),
          where('__name__', 'in', chunk)
        ))
        allTranslations.push(...translationsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as CategoryTranslation)))
      }

      // Combine data
      const translationMap = new Map(allTranslations.map(t => [t.categoryId, t]))
      
      return allCategories
        .map(category => {
          const translation = translationMap.get(category.id)
          return translation ? this.combineData(category, translation) : null
        })
        .filter((category): category is CategoryChallenge => category !== null)
        .sort((a, b) => a.order - b.order)

    } catch (error) {
      console.error('Failed to get categories by IDs:', error)
      throw error
    }
  }

  /**
   * Get available languages for a category
   */
  async getAvailableLanguages(categoryId: string): Promise<string[]> {
    try {
      const translationsSnapshot = await getDocs(query(
        collection(db, 'categoryTranslations'),
        where('categoryId', '==', categoryId)
      ))

      return translationsSnapshot.docs.map(doc => doc.data().language)
    } catch (error) {
      console.error(`Failed to get available languages for category ${categoryId}:`, error)
      return []
    }
  }

  /**
   * Get category stats for admin dashboard
   */
  async getCategoryStats(): Promise<{ totalCategories: number; activeCategories: number; languageCount: number }> {
    try {
      const [categoriesSnapshot, translationsSnapshot] = await Promise.all([
        getDocs(collection(db, 'categories')),
        getDocs(collection(db, 'categoryTranslations'))
      ])

      const categories = categoriesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Category))
      const activeCategories = categories.filter(c => c.status === 'active').length
      
      // Count unique languages
      const languages = new Set(
        translationsSnapshot.docs.map(doc => doc.data().language)
      )

      return {
        totalCategories: categories.length,
        activeCategories,
        languageCount: languages.size
      }
    } catch (error) {
      console.error('Failed to get category stats:', error)
      throw error
    }
  }

  /**
   * Private helper methods
   */
  private combineData(category: Category, translation: CategoryTranslation): CategoryChallenge {
    return {
      id: category.id,
      name: translation.name,
      description: translation.description,
      shortDescription: translation.shortDescription || translation.description.substring(0, 100),
      icon: category.icon,
      color: category.color,
      slug: category.slug,
      order: category.order,
      status: category.status,
      language: translation.language,
      totalChallenges: category.metadata?.totalChallenges || 0,
      createdAt: this.convertTimestamp(category.metadata?.createdAt),
      updatedAt: this.convertTimestamp(category.metadata?.updatedAt)
    }
  }

  private convertTimestamp(timestamp: any): Date {
    if (!timestamp) return new Date()
    if (timestamp instanceof Timestamp) {
      return timestamp.toDate()
    }
    if (timestamp instanceof Date) {
      return timestamp
    }
    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      return timestamp.toDate()
    }
    if (timestamp.seconds && timestamp.nanoseconds !== undefined) {
      return new Timestamp(timestamp.seconds, timestamp.nanoseconds).toDate()
    }
    return new Date()
  }

  private updateCache(categories: Category[]) {
    categories.forEach(category => {
      this.categoryCache.set(category.id, category)
    })
  }

  private updateTranslationCache(language: string, translations: CategoryTranslation[]) {
    if (!this.translationCache.has(language)) {
      this.translationCache.set(language, new Map())
    }
    const languageCache = this.translationCache.get(language)!
    translations.forEach(translation => {
      languageCache.set(translation.categoryId, translation)
    })
  }

  private chunkArray<T>(array: T[], size: number): T[][] {
    const chunks: T[][] = []
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size))
    }
    return chunks
  }

  /**
   * Clear cache (useful for testing or after updates)
   */
  clearCache() {
    this.categoryCache.clear()
    this.translationCache.clear()
  }
}